"use client";

import { convexClient } from "@convex-dev/better-auth/client/plugins";
import { magicLinkClient, oneTapClient, twoFactorClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

/**
 * Serialize concurrent `authClient.convex.token()` calls so a single navigation tick triggers at most
 * one in-flight HTTP request to `/api/auth/convex/token`.
 */
function attachConvexTokenDedupe(client: ReturnType<typeof createAuthClient>): void {
    const convexRaw = Reflect.get(client, "convex");
    if (convexRaw === undefined || convexRaw === null || typeof convexRaw !== "object") {
        return;
    }
    const originalRaw = Reflect.get(convexRaw, "token");
    if (typeof originalRaw !== "function") {
        return;
    }
    const original = originalRaw as (opts?: unknown) => Promise<unknown>;

    let inflight: Promise<unknown> | null = null;

    Reflect.set(convexRaw, "token", async (opts?: unknown) => {
        if (!inflight) {
            inflight = original(opts).finally(() => {
                inflight = null;
            });
        }
        return inflight;
    });
}

/**
 * Better Auth React Client for Convex
 *
 * Provides hooks and utilities for client-side authentication:
 * - useSession() for reactive session state
 * - signIn.email() / signIn.social() for login
 * - signUp.email() for registration
 * - signOut() for logout
 * - oneTap() for Google One Tap sign-in
 * - twoFactor.* for MFA enrollment and verification
 *
 * The client uses the Convex plugin which handles token management
 * and ensures auth state syncs with Convex queries/mutations.
 */
export const authClient = createAuthClient({ plugins: [convexClient(), twoFactorClient(), oneTapClient({ clientId: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID! }), magicLinkClient()] });

attachConvexTokenDedupe(authClient);

// Export individual methods for convenience
export const { signIn, signUp, signOut, useSession, getSession, oneTap, twoFactor } = authClient;
